import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDrag } from '@use-gesture/react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { useAppContext } from '../AppContext';
import './LotteryPanel.css';
import * as LucideIcons from 'lucide-react';

const LotteryPanel = () => {
    const { t } = useTranslation();
    const { appFunctions, handlePageChange, currentUserProfile, showToast } = useAppContext();
    const [lotteryStatus, setLotteryStatus] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeSlide, setActiveSlide] = useState(0);
    const [ticketAmount, setTicketAmount] = useState(10);
    const [intensity, setIntensity] = useState(0);
    const tabRef = useRef(null);

    // --- Countdown & Intensity Logic ---
    const calculateTimeLeft = (drawDate) => {
        // Always return a structured object to prevent crash on initial render
        if (!drawDate) return { timeLeft: {}, difference: 0 }; 
        const difference = new Date(drawDate).getTime() - new Date().getTime();
        let timeLeft = {};

        if (difference > 0) {
            timeLeft = {
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60),
            };
        }
        return { timeLeft, difference };
    };

    const [timeLeft, setTimeLeft] = useState({}); // Initialize with a safe empty object

    useEffect(() => {
        if (!lotteryStatus?.drawDate) return;

        const timer = setInterval(() => {
            const { timeLeft, difference } = calculateTimeLeft(lotteryStatus.drawDate);
            setTimeLeft(timeLeft);

            const totalTime = 24 * 60 * 60 * 1000; // 24-hour cycle
            const timePassed = totalTime - Math.max(0, difference);
            const percentagePassed = timePassed / totalTime;
            
            // Intensity ramps up, especially in the last hour
            let newIntensity = Math.pow(percentagePassed, 2);
            if (difference < 60 * 60 * 1000) { // Last hour
                newIntensity = 1 - (difference / (60 * 60 * 1000));
            }
            setIntensity(newIntensity);

            // Haptic feedback for mobile
            if (navigator.vibrate) {
                if (newIntensity > 0.95) {
                    navigator.vibrate(200); // Strong vibration in the last moments
                } else if (newIntensity > 0.8) {
                    navigator.vibrate(50); // Softer vibration
                }
            }

        }, 1000);

        return () => clearInterval(timer);
    }, [lotteryStatus?.drawDate]);
    
    useEffect(() => {
        if (tabRef.current) {
            tabRef.current.style.setProperty('--glow-intensity', intensity);
            // Control the play state of the new breathing-border animation
            tabRef.current.style.animationPlayState = intensity > 0.1 ? 'running' : 'paused';
        }
    }, [intensity]);


    const timerComponents = useMemo(() => {
        const timeParts = [];
        if (timeLeft.hours !== undefined) timeParts.push(`${timeLeft.hours.toString().padStart(2, '0')}h`);
        if (timeLeft.minutes !== undefined) timeParts.push(`${timeLeft.minutes.toString().padStart(2, '0')}m`);
        if (timeLeft.seconds !== undefined) timeParts.push(`${timeLeft.seconds.toString().padStart(2, '0')}s`);
        return timeParts;
    }, [timeLeft]);

    // --- Fetch Lottery Data ---
    const fetchLotteryStatus = async () => {
        try {
            const getLotteryStatus = httpsCallable(appFunctions, 'getLotteryStatus');
            const result = await getLotteryStatus();
            setLotteryStatus(result.data);
        } catch (error) {
            console.error("Error fetching lottery status:", error);
        }
    };

    useEffect(() => {
        fetchLotteryStatus();
        const interval = setInterval(fetchLotteryStatus, 60000); // Refresh every minute
        return () => clearInterval(interval);
    }, [appFunctions, t, showToast]);

    // --- Handle Ticket Purchase ---
    const handleBuyTicket = async (e) => {
        e.preventDefault();
        const amount = parseInt(ticketAmount, 10);
        if (isNaN(amount) || amount <= 0) {
            showToast(t('lotteryWidget.invalidAmount', 'Please enter a valid amount.'), 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            const enterLottery = httpsCallable(appFunctions, 'enterLottery');
            await enterLottery({ amount });
            showToast(t('lotteryWidget.purchaseSuccess', { count: amount }), 'success');
            fetchLotteryStatus(); // Refresh data after purchase
            setTicketAmount(10); // Reset to default
        } catch (err) {
            console.error("Error entering lottery:", err);
            showToast(err.message, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const userTickets = lotteryStatus?.participants?.[currentUserProfile?.uid]?.tickets || 0;

    // Helper to switch slides
    const nextSlide = () => setActiveSlide((prev) => (prev + 1) % 2);
    const prevSlide = () => setActiveSlide((prev) => (prev - 1 + 2) % 2);

    const bind = useDrag(({ swipe: [swipeX] }) => {
        if (swipeX === -1) {
            nextSlide();
        } else if (swipeX === 1) {
            prevSlide();
        }
    });

    return (
        <div className={`lottery-panel-container ${isOpen ? 'open' : ''}`}>
            <div ref={tabRef} className="lottery-panel-tab" onClick={() => setIsOpen(!isOpen)}>
                <motion.div
                    className="lottery-icon-wrapper"
                    animate={{
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, -5, 0],
                    }}
                    transition={{
                        duration: 2,
                        ease: "easeInOut",
                        repeat: Infinity,
                        repeatDelay: 3
                    }}
                >
                    <LucideIcons.Ticket size={28} className="text-yellow-300" />
                </motion.div>
            </div>

            <div {...bind()} className={`lottery-panel ${isOpen ? 'visible' : ''} relative touch-pan-y pt-8`}>
                {/* Navigation Dots */}
                <div className="absolute top-2 left-0 right-0 flex justify-center gap-3 z-30">
                    <button 
                        onClick={() => setActiveSlide(0)} 
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${activeSlide === 0 ? 'bg-white w-4' : 'bg-white/30 hover:bg-white/50'}`} 
                    />
                    <button 
                        onClick={() => setActiveSlide(1)} 
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${activeSlide === 1 ? 'bg-cyan-400 w-4' : 'bg-white/30 hover:bg-white/50'}`} 
                    />
                </div>

                <AnimatePresence mode='wait'>
                    {activeSlide === 0 ? (
                        <motion.div
                            key="lottery"
                            initial={{ x: -50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: -50, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="text-white p-2 sm:p-4 flex flex-col w-full relative"
                        >
                            {lotteryStatus ? (
                                <>
                                    <div className="text-center mb-2 sm:mb-4">
                                        <h3 className="text-lg sm:text-xl font-bold text-yellow-300 font-playfair">{t('lotteryWidget.title')}</h3>
                                        <p className="text-xs sm:text-sm text-gray-400">{t('lotteryWidget.subtitle')}</p>
                                    </div>

                                    {/* Prize Pool */}
                                    <motion.div
                                        className="bg-gray-900/50 p-2 sm:p-4 rounded-xl mb-2 sm:mb-4 text-center shadow-glow"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 }}
                                    >
                                        <p className="text-xs text-yellow-400 uppercase tracking-widest">{t('lotteryWidget.currentJackpot')}</p>
                                        <div className="flex justify-center items-center">
                                            <LucideIcons.Gem size={20} className="mr-2 text-yellow-400" />
                                            <p className="text-2xl sm:text-4xl font-bold text-white">
                                                {lotteryStatus.totalPot?.toLocaleString() || 0}
                                            </p>
                                        </div>
                                    </motion.div>

                                    {/* Countdown Timer */}
                                    <div className="bg-gray-900/50 p-2 sm:p-4 rounded-xl mb-2 sm:mb-4 text-center">
                                        <p className="text-xs text-cyan-300 uppercase tracking-widest">{t('lotteryWidget.nextDrawing')}</p>
                                        <div className="flex justify-center space-x-1 sm:space-x-2 text-xl sm:text-2xl font-mono text-white mt-1">
                                            {timerComponents.length > 0 ? (
                                                timerComponents.map((part, index) => <span key={index}>{part}</span>)
                                            ) : (
                                                <span>{t('lotteryWidget.drawingInProgress')}</span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* User Info */}
                                    <div className="grid grid-cols-2 gap-2 mb-2 sm:mb-4 text-center">
                                        <div className="bg-gray-900/50 p-2 rounded-lg">
                                            <p className="text-xs text-gray-400">{t('lotteryWidget.yourTickets')}</p>
                                            <p className="text-base sm:text-lg font-bold">{userTickets}</p>
                                        </div>
                                        <div className="bg-gray-900/50 p-2 rounded-lg">
                                            <p className="text-xs text-gray-400">{t('lotteryPage.participants', { count: lotteryStatus.participantCount || 0 })}</p>
                                            <p className="text-base sm:text-lg font-bold">{lotteryStatus.participantCount || 0}</p>
                                        </div>
                                    </div>

                                    {/* Buy Ticket Form */}
                                    <form onSubmit={handleBuyTicket} className="flex flex-col gap-2">
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                value={ticketAmount}
                                                onChange={(e) => setTicketAmount(e.target.value)}
                                                className="w-full bg-gray-800/70 text-white p-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                                placeholder={t('lotteryWidget.ticketAmountPlaceholder', 'Enter amount')}
                                                min="1"
                                            />
                                            <LucideIcons.Gem size={20} className="text-purple-400" />
                                        </div>
                                        <motion.button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold py-2 sm:py-3 px-4 rounded-full shadow-lg disabled:opacity-50"
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            {isSubmitting ? t('lotteryWidget.purchasing') : t('lotteryWidget.buyTicket', { count: ticketAmount })}
                                        </motion.button>
                                    </form>
                                    
                                    <div className="flex-grow"></div>

                                    {/* Last Winner */}
                                    {lotteryStatus.lastWinner && (
                                        <div className="bg-gray-900/50 p-2 sm:p-3 rounded-xl mt-2 sm:mt-4 text-center border border-purple-500/30">
                                            <p className="text-xs text-purple-300 uppercase tracking-widest">{t('lotteryWidget.lastWinner')}</p>
                                            <div className="flex items-center justify-center mt-1">
                                                <img src={lotteryStatus.lastWinner.photoURL || 'https://placehold.co/24x24'} alt="winner" className="w-6 h-6 rounded-full mr-2" />
                                                <p className="font-semibold text-xs sm:text-sm">{lotteryStatus.lastWinner.displayName}</p>
                                            </div>
                                            <p className="text-xs text-gray-400 mt-1">{t('lotteryWidget.wonAmount', { count: lotteryStatus.lastWinner.prize?.toLocaleString() || 0 })}</p>
                                        </div>
                                    )}

                                    <button onClick={() => handlePageChange('lottery')} className="text-center text-xs text-gray-400 hover:text-white mt-2 sm:mt-4">
                                        {t('lotteryWidget.viewDetails')}
                                    </button>
                                </>
                            ) : (
                                <div className="flex items-center justify-center h-full">
                                    <LucideIcons.Loader className="animate-spin text-white" size={48} />
                                </div>
                            )}
                        </motion.div>
                    ) : (
                        <motion.div
                            key="slots-ad"
                            initial={{ x: 50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: 50, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="text-white p-4 flex flex-col w-full relative items-center justify-center text-center bg-gradient-to-b from-slate-900 via-[#0f0c29] to-slate-900"
                        >
                            <motion.div 
                                animate={{ rotate: 360 }}
                                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                className="mb-6"
                            >
                                <LucideIcons.Zap size={64} className="text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)]" />
                            </motion.div>
                            
                            <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 font-playfair mb-2">
                                Harmonic Slots
                            </h3>
                            
                            <p className="text-sm text-gray-300 mb-6 leading-relaxed">
                                The cosmos aligns for those who dare. Spin the reels to weave your destiny and claim the 
                                <span className="text-yellow-400 font-bold block mt-1">Community Jackpot</span>
                            </p>

                            <div className="grid grid-cols-2 gap-3 mb-8 w-full">
                                <div className="bg-white/5 p-2 rounded-lg border border-white/10">
                                    <LucideIcons.InfinityIcon size={20} className="mx-auto text-purple-400 mb-1" />
                                    <span className="text-[10px] text-gray-400 uppercase tracking-wider">Infinite Potential</span>
                                </div>
                                <div className="bg-white/5 p-2 rounded-lg border border-white/10">
                                    <LucideIcons.Gem size={20} className="mx-auto text-pink-400 mb-1" />
                                    <span className="text-[10px] text-gray-400 uppercase tracking-wider">Massive Payouts</span>
                                </div>
                            </div>

                            <motion.button
                                onClick={() => handlePageChange('walletHub', { tab: 'slots' })}
                                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-3 px-6 rounded-full shadow-[0_0_20px_rgba(6,182,212,0.4)] flex items-center justify-center gap-2"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <LucideIcons.Gamepad2 size={20} />
                                <span>SPIN NOW</span>
                            </motion.button>
                        </motion.div>
                    )}
                </AnimatePresence>
                
                {/* Swipe overlay for touch/mouse drag simulation could be added here, but click nav is safer for now */}
            </div>
        </div>
    );
};

export default LotteryPanel;